import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './ConversationalMode.module.scss'
import ConversationWindow from './ConversationWindow.tsx'
import ConversationInput from './ConversationInput.tsx'
import ChatSearchModal from './ChatSearchModal.tsx'
import { Message, ConversationSession } from '../utils/conversationStorage.ts'
import { getDesktopSuggestions } from '../dashboardActionsConfig.tsx'
import type { DesktopSuggestion } from '../dashboardActionsConfig.tsx'

interface ConversationalModeProps {
  isActive: boolean
  messages: Message[]
  onSendMessage: (message: string) => Promise<void>
  onClose: () => void
  isLoading?: boolean
  canSendMessage: boolean
  remainingMessages: number
  hasReachedLimit: boolean
  limit: number
  matchedSuggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  allSessions: ConversationSession[]
  currentSessionId: string | null
  onSelectSession: (sessionId: string) => void
  onCreateNewSession: () => void
  onDeleteSession: (sessionId: string) => void
  userId?: string
  userData?: any
  searchModalRef?: React.MutableRefObject<(() => void) | null>
}

const ConversationalMode: React.FC<ConversationalModeProps> = ({
  isActive,
  messages,
  onSendMessage,
  onClose,
  isLoading = false,
  canSendMessage,
  remainingMessages,
  hasReachedLimit,
  limit,
  matchedSuggestion,
  onSuggestionAction,
  allSessions,
  currentSessionId,
  onSelectSession,
  onCreateNewSession,
  onDeleteSession,
  userId,
  userData,
  searchModalRef
}) => {
  const navigate = useNavigate()
  const [inputValue, setInputValue] = useState('')
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  
  const allSuggestions = useMemo(() => {
    return getDesktopSuggestions(navigate, userId, userData)
  }, [navigate, userId, userData])

  const openSearchModal = () => {
    setIsSearchModalOpen(true)
  }

  useEffect(() => {
    if (searchModalRef) {
      searchModalRef.current = openSearchModal
      return () => {
        searchModalRef.current = null
      }
    }
  }, [searchModalRef])

  if (!isActive) {
    return null
  }

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading || !canSendMessage) {
      return
    }

    const message = inputValue.trim()
    setInputValue('')
    await onSendMessage(message)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className={styles.conversationalMode}>
      <ConversationWindow
        messages={messages}
        onClose={onClose}
        isLoading={isLoading}
        matchedSuggestion={matchedSuggestion}
        onSuggestionAction={onSuggestionAction}
        allSuggestions={allSuggestions}
        hasReachedLimit={hasReachedLimit}
        remainingMessages={remainingMessages}
        limit={limit}
        userData={userData}
      />
      <div className={styles.floatingInputContainer}>
        <ConversationInput
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          onKeyPress={handleKeyPress}
          disabled={isLoading || !canSendMessage}
          placeholder={
            canSendMessage
              ? 'Escribí tu mensaje...'
              : 'Has alcanzado el límite de mensajes'
          }
        />
      </div>
      <ChatSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        sessions={allSessions}
        currentSessionId={currentSessionId}
        onSelectSession={onSelectSession}
        onCreateNewSession={onCreateNewSession}
        onDeleteSession={onDeleteSession}
        userId={userId}
        userData={userData}
      />
    </div>
  )
}

export default ConversationalMode
